import type { AchievementRanking, AchievementRow } from '@/services/targets';
import { formatCurrency } from '@/utils/format';

/**
 * Board #263 — target vs achievement, one row per entity.
 *
 * The one rule this widget enforces: a missing target is UNKNOWN, not zero.
 * `percentage === null` renders as an em dash and "No target set", never
 * "0%" and never "null%" — the mistake the country analytics page's previous
 * placeholder existed to avoid.
 */

interface Props {
  rows: AchievementRow[];
  top?: AchievementRanking[];
  bottom?: AchievementRanking[];
  /** Column header for the entity, e.g. "Broker" or "POSP". */
  entityLabel?: string;
  emptyMessage?: string;
  /** U-02: on wide screens put the top/bottom rankings in a column beside the
   *  table instead of under it, so the section reads without scrolling. */
  rankingsAside?: boolean;
}

function percentText(percentage: number | null): string {
  return percentage === null ? '—' : `${percentage}%`;
}

function ProgressBar({ percentage, name }: { percentage: number | null; name: string }) {
  if (percentage === null) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">No target set</span>;
  }
  const width = Math.min(percentage, 100);
  const tone =
    percentage >= 100 ? 'bg-green-500' : percentage >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div
      role="progressbar"
      aria-label={`${name} achievement`}
      aria-valuenow={percentage}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700"
    >
      <div className={`h-2 rounded-full ${tone}`} style={{ width: `${width}%` }} />
    </div>
  );
}

export function TargetVsAchievementWidget({
  rows,
  top = [],
  bottom = [],
  entityLabel = 'Name',
  emptyMessage = 'No targets set for this period yet.',
  rankingsAside = false,
}: Props) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>;
  }

  const anyTarget = rows.some((r) => Number(r.target) > 0);
  const hasRankings = top.length > 0 || bottom.length > 0;
  const th =
    'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
  const td = 'px-3 py-1.5 text-sm tabular-nums text-gray-800 dark:text-gray-200';

  return (
    <div className="space-y-4">
      {!anyTarget && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
          No targets set for this period. Achievement is shown, but there is nothing to measure it
          against yet.
        </p>
      )}

      <div
        className={
          rankingsAside && hasRankings
            ? 'space-y-4 xl:grid xl:grid-cols-[minmax(0,1fr)_15rem] xl:gap-6 xl:space-y-0'
            : 'space-y-4'
        }
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th scope="col" className={`${th} text-left`}>
                  {entityLabel}
                </th>
                <th scope="col" className={`${th} text-right`}>
                  Target
                </th>
                <th scope="col" className={`${th} text-right`}>
                  Achieved
                </th>
                <th scope="col" className={`${th} text-right`}>
                  %
                </th>
                <th scope="col" className={`${th} w-32 text-left sm:w-40`}>
                  Progress
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((row) => (
                <tr key={`${row.entity_type}-${row.entity_id}`}>
                  <td className={td}>
                    {row.name}{' '}
                    {row.code && (
                      <span className="text-gray-400 dark:text-gray-500">({row.code})</span>
                    )}
                  </td>
                  <td className={`${td} text-right`}>
                    {Number(row.target) > 0 ? formatCurrency(row.target) : '—'}
                  </td>
                  <td className={`${td} text-right`}>{formatCurrency(row.achieved)}</td>
                  <td className={`${td} text-right font-semibold`}>
                    {percentText(row.percentage)}
                  </td>
                  <td className="px-3 py-1.5">
                    <ProgressBar percentage={row.percentage} name={row.name} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasRankings && (
          <div
            className={
              rankingsAside
                ? 'grid gap-4 sm:grid-cols-2 xl:grid-cols-1 xl:content-start xl:border-l xl:border-gray-100 xl:pl-6 xl:dark:border-gray-700'
                : 'grid gap-4 sm:grid-cols-2'
            }
          >
            <Ranking title="Top by achievement" entries={top} />
            <Ranking title="Bottom by achievement" entries={bottom} />
          </div>
        )}
      </div>
    </div>
  );
}

function Ranking({ title, entries }: { title: string; entries: AchievementRanking[] }) {
  return (
    <div>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </h3>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Nothing to rank — no targets set.
        </p>
      ) : (
        <ul className="space-y-1">
          {entries.map((e) => (
            <li key={e.entity_id} className="flex justify-between text-sm">
              <span className="truncate text-gray-700 dark:text-gray-300">{e.name}</span>
              <span className="shrink-0 pl-2 font-semibold tabular-nums text-gray-900 dark:text-white">
                {e.percentage}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TargetVsAchievementWidget;
